import Report, {
  ExtendedReportUsage,
  ReportUsage,
} from "@/data/models/Report";
import { parseDate } from "@/utils/DateHelper";

/**
 * Maximum gap between two reports of the same kind that still counts as one
 * generation run ("bundle"), used only for reports that carry no run folder
 * (see `getReportRunFolder`). Uploaded documents and single time entries are
 * written one at a time, so this is a fallback, not the main rule.
 */
export const REPORT_BUNDLE_GAP_MS = 30000;

/**
 * Order of the reports inside one bundle: the internal report is the parent the
 * report list indents everything else under, so it has to come first. The rest
 * follows a fixed order so the same bundle always renders the same way instead
 * of depending on sub-second write order.
 */
const REPORT_USAGE_ORDER: Array<ExtendedReportUsage | ReportUsage> = [
  ExtendedReportUsage.INTERNAL,
  ExtendedReportUsage.EXTERNAL,
  ExtendedReportUsage.EXT_SHORT,
  ExtendedReportUsage.EXT_IMAGES,
  ExtendedReportUsage.EXT_TIME_TRACKING,
  ExtendedReportUsage.EXT_SINGLE_TIME_ENTRY,
  ExtendedReportUsage.MEASUREMENT,
  ExtendedReportUsage.SERVICE,
  ExtendedReportUsage.SERVICE_JSON,
  ExtendedReportUsage.HY_DRY_CERTIFICATE,
  ExtendedReportUsage.LO,
  ExtendedReportUsage.SA,
  ExtendedReportUsage.TA,
  ExtendedReportUsage.WH,
  ExtendedReportUsage.SV_IMAGE,
  ExtendedReportUsage.DOCUMENT,
];

/** Usages not listed above sort after the known ones, in time order. */
const UNKNOWN_USAGE_RANK = REPORT_USAGE_ORDER.length;

/** The usage a report is displayed and sorted by. */
function getReportUsage(report: Report): ExtendedReportUsage | ReportUsage {
  return report.extendedReportUsage ?? report.usage;
}

function getUsageRank(report: Report): number {
  const rank = REPORT_USAGE_ORDER.indexOf(getReportUsage(report));

  return rank === -1 ? UNKNOWN_USAGE_RANK : rank;
}

/**
 * The point in time a report is sorted by: when it was last sent, falling back
 * to when it was created.
 *
 * Reports carry Firestore `Timestamp`s, but legacy documents may hold an ISO
 * string, and a timestamp that travelled through JSON arrives as a plain
 * `{seconds, nanoseconds}` object without `toMillis()`. Returns `0` for a
 * report with no usable date at all, which sorts it to the end of the list.
 */
export function getReportSortTimeMs(report: Report): number {
  const date = report.lastTimeSend ?? report.reportDate;

  if (date == null) {
    return 0;
  }

  if (typeof date === "string") {
    return parseDate(date)?.getTime() ?? 0;
  }

  if (typeof date.toMillis === "function") {
    return date.toMillis();
  }

  // Deserialized timestamp: `seconds` from the client SDK, `_seconds` from the
  // admin SDK / REST responses.
  const plainTimestamp = date as unknown as {
    seconds?: number;
    _seconds?: number;
  };
  const seconds = plainTimestamp.seconds ?? plainTimestamp._seconds;

  return typeof seconds === "number" ? seconds * 1000 : 0;
}

/**
 * The storage folder a generation run writes all of its files into, e.g.
 * `SHYB02647_31-07-16-49` in
 * `/reports/{clientId}/SHYB02647_31-07-16-49/SHYB02647_2607311649_..._PDE.json`.
 *
 * This is the only exact run identifier the reports carry: the file *names* are
 * stamped per file, so members of one run can disagree (a run in folder
 * `…_17-07` holds a file named `…_2607311708_…`), and the document timestamps of
 * two consecutive runs can be seconds apart.
 *
 * Returns `null` when there is no folder between the client and the file, which
 * is the case for uploaded documents and single time entries
 * (`/reports/{clientId}/{fileName}`). Those have no run to belong to.
 */
export function getReportRunFolder(report: Report): string | null {
  if (!report.storageLocation) {
    return null;
  }

  const tokens = report.storageLocation
    .split("/")
    .filter((token) => token.length > 0);

  // Expected layout: reports / {clientId} / {runFolder} / {fileName}
  const reportsIndex = tokens.indexOf("reports");
  if (reportsIndex === -1) {
    return null;
  }

  const folderIndex = reportsIndex + 2;

  // The last token is the file itself, so a folder has to sit before it.
  return folderIndex < tokens.length - 1 ? tokens[folderIndex] : null;
}

/**
 * Identity of the report *kind*. One run folder can hold two runs, e.g. a
 * "Bericht Leckortung" and a "Zwischenbericht Leckortung" both generated in the
 * minute the folder is named after, so the kind is part of the bundle key.
 */
function getKindKey(report: Report): string {
  const reportType = report.extendedReportType ?? report.reportType;

  return [
    reportType ?? "",
    report.interimReportPhase ?? "",
    report.phaseComplete ?? "",
  ].join("|");
}

/**
 * Identity of the generation run a report belongs to: its kind plus its run
 * folder. Reports without a run folder share one key per kind and are grouped by
 * timestamp proximity instead.
 */
function getBundleKey(report: Report, runFolder: string | null): string {
  return runFolder == null
    ? `${getKindKey(report)}|by-time`
    : `${getKindKey(report)}|folder:${runFolder}`;
}

/** A generation run: an internal report plus the artifacts written with it. */
interface ReportBundle {
  /**
   * Newest member, i.e. the first one collected. Only used for bundles without
   * a run folder: membership is measured against this anchor rather than against
   * the previous member, so such a bundle spans at most `REPORT_BUNDLE_GAP_MS`
   * in total instead of chaining from member to member.
   */
  newestTimeMs: number;
  reports: Report[];
}

/**
 * Orders the reports of a project for the report list.
 *
 * The list renders flat and marks every non-internal report with an indent, so
 * a report visually belongs to the internal report above it. This groups the
 * reports into generation runs first (by run folder and kind, falling back to
 * timestamp proximity for reports without a run folder), then emits the newest
 * run first, each run led by its internal report and followed by its artifacts
 * in `REPORT_USAGE_ORDER`.
 *
 * Grouping wins over strict time order: an artifact stays with its run instead
 * of drifting into a run that was generated seconds later.
 *
 * Does not modify the input array.
 */
export function groupAndSortReports(reports: Array<Report>): Array<Report> {
  const byTimeDescending = [...reports].sort(
    (first, second) => getReportSortTimeMs(second) - getReportSortTimeMs(first)
  );

  const bundles: ReportBundle[] = [];
  /** The still-extendable bundle per kind. */
  const openBundles = new Map<string, ReportBundle>();

  for (const report of byTimeDescending) {
    const timeMs = getReportSortTimeMs(report);
    const runFolder = getReportRunFolder(report);
    const key = getBundleKey(report, runFolder);
    const openBundle = openBundles.get(key);

    // A run folder identifies the run exactly, so its members belong together
    // however far apart they were written or re-sent. Without one, the only
    // signal left is that the reports were written at about the same time.
    const belongsToOpenBundle =
      openBundle != null &&
      (runFolder != null ||
        openBundle.newestTimeMs - timeMs <= REPORT_BUNDLE_GAP_MS);

    if (belongsToOpenBundle) {
      openBundle.reports.push(report);
      continue;
    }

    // A new run: either a folder not seen yet, or a folderless report too far
    // from the previous one. The old bundle keeps its place in `bundles`.
    const bundle: ReportBundle = {
      newestTimeMs: timeMs,
      reports: [report],
    };
    bundles.push(bundle);
    openBundles.set(key, bundle);
  }

  // `byTimeDescending` is stable and each bundle is created on its newest
  // member, so `bundles` is already ordered newest first.
  return bundles.flatMap((bundle) =>
    bundle.reports
      .map((report, index) => ({ report, index }))
      .sort((first, second) => {
        const rankDifference =
          getUsageRank(first.report) - getUsageRank(second.report);

        // Same usage: keep the time-descending order they were collected in.
        return rankDifference !== 0 ? rankDifference : first.index - second.index;
      })
      .map((entry) => entry.report)
  );
}
